import { platform } from 'playcanvas';

import { Events } from './events';
import { Shortcuts, ShortcutBinding } from './shortcuts';

// Mac uses different symbols for modifier keys
const isMac = platform.name === 'osx';

// Default shortcut bindings - the source of truth for key mappings
const defaultShortcuts: Record<string, ShortcutBinding> = {
    // Navigation
    // Shift+F (camera.reset) and V (camera.toggleControlMode) unbound for
    // the FTGS viewer (2026-10-26); the events stay registered
    'camera.focus': { keys: ['f'] },

    // Show
    // Tab (camera.toggleOverlay), M (camera.toggleMode) and I
    // (camera.toggleShowInfo) unbound for the FTGS viewer (2026-10-26)
    'stereo.toggleConvergenceAxes': { keys: ['g'] },
    // H / Shift+H (select.hide / select.unhide) unbound for the FTGS viewer
    // (2026-10-26)

    // Playback
    'timeline.togglePlay': { keys: [' '] },
    // , / . (prev/next frame), < / > (prev/next key) and Enter / Shift+Enter
    // (add/remove key) - the editor's keyframe timeline - unbound for the
    // FTGS viewer (2026-10-26)

    // Selection
    // Ctrl+A / Ctrl+Shift+A / Ctrl+I (select all / none / invert) and
    // Delete / Backspace (select.delete) unbound for the FTGS viewer
    // (2026-10-26)

    // Tools
    // 1/2/3 don't fire tool.move/rotate/scale directly: while a shape
    // selection tool (box/sphere) is active they switch its gizmo mode
    // instead of switching tools (see ToolManager)
    'tool.moveShortcut': { keys: ['1'] },
    'tool.rotateShortcut': { keys: ['2'] },
    'tool.scaleShortcut': { keys: ['3'] },
    // Unbound for the FTGS viewer (2026-10-26) - they only drove editor
    // tools that are invisible here with nothing to select or transform:
    //   R / L / P / B / O   rect / lasso / polygon / brush / flood selection
    //   Ctrl+E              eyedropper selection
    //   [ / ]               brush size smaller / bigger
    //   Esc                 tool.deactivate
    //   Shift+C             tool.toggleCoordSpace
    // The tools and events themselves stay registered; formatShortcut()
    // shows no key for them.

    // Other
    // Ctrl+Z / Ctrl+Shift+Z (edit.undo / edit.redo) unbound for the FTGS
    // viewer (2026-10-26)
    'dataPanel.toggle': { keys: ['d'], ctrl: 'required', capture: true },
    'timelinePanel.toggle': { keys: ['t'], ctrl: 'required', capture: true },

    // Camera fly keys - use physical positions (codes) for WASD layout on non-QWERTY keyboards
    'camera.fly.forward': { codes: ['KeyW'], held: true, shift: 'optional', alt: 'optional' },
    'camera.fly.backward': { codes: ['KeyS'], held: true, shift: 'optional', alt: 'optional' },
    'camera.fly.left': { codes: ['KeyA'], held: true, shift: 'optional', alt: 'optional' },
    'camera.fly.right': { codes: ['KeyD'], held: true, shift: 'optional', alt: 'optional' },
    'camera.fly.down': { codes: ['KeyQ'], held: true, shift: 'optional', alt: 'optional' },
    'camera.fly.up': { codes: ['KeyE'], held: true, shift: 'optional', alt: 'optional' },
    // Camera look keys (FTGS, 2026-10-26) - turn in place (camera.look) in
    // either control mode, without switching it; I/K up/down, J/L left/right
    'camera.look.up': { codes: ['KeyI'], held: true, shift: 'optional', alt: 'optional' },
    'camera.look.down': { codes: ['KeyK'], held: true, shift: 'optional', alt: 'optional' },
    'camera.look.left': { codes: ['KeyJ'], held: true, shift: 'optional', alt: 'optional' },
    'camera.look.right': { codes: ['KeyL'], held: true, shift: 'optional', alt: 'optional' },
    'camera.modifier.fast': { codes: ['ShiftLeft', 'ShiftRight'], held: true, alt: 'optional' },
    'camera.modifier.slow': { codes: ['AltLeft', 'AltRight'], held: true, shift: 'optional' }
};

class ShortcutManager {
    private bindings: Record<string, ShortcutBinding>;

    constructor(events: Events) {
        // Clone the defaults so they can be modified without affecting the originals
        this.bindings = {};
        for (const id in defaultShortcuts) {
            this.bindings[id] = { ...defaultShortcuts[id] };
        }

        // Create shortcuts and register all bindings
        const shortcuts = new Shortcuts(events);
        for (const id in this.bindings) {
            const binding = this.bindings[id];
            shortcuts.register({
                event: id,
                keys: binding.keys,
                codes: binding.codes,
                ctrl: binding.ctrl,
                shift: binding.shift,
                alt: binding.alt,
                held: binding.held,
                repeat: binding.repeat,
                capture: binding.capture
            });
        }
    }

    /**
     * Get a shortcut binding by its event ID.
     */
    get(id: string): ShortcutBinding | undefined {
        return this.bindings[id];
    }

    /**
     * Format a shortcut for display (e.g., "Ctrl + Shift + Z" or "⌘⇧Z" on Mac).
     */
    formatShortcut(id: string): string {
        const binding = this.bindings[id];
        if (!binding) return '';

        const parts: string[] = [];

        // Use Mac symbols: ⌘ (Cmd), ⌥ (Option), ⇧ (Shift)
        if (binding.ctrl === 'required') parts.push(isMac ? '⌘' : 'Ctrl');
        if (binding.alt === 'required') parts.push(isMac ? '⌥' : 'Alt');
        if (binding.shift === 'required') parts.push(isMac ? '⇧' : 'Shift');

        // Get the first key or code for display
        let keyDisplay = binding.keys?.[0] ?? binding.codes?.[0];
        if (!keyDisplay) return '';

        if (keyDisplay === ' ') {
            keyDisplay = 'Space';
        } else if (keyDisplay === 'Escape') {
            keyDisplay = 'Esc';
        } else if (keyDisplay.startsWith('Key')) {
            // Physical key codes like 'KeyW' -> 'W'
            keyDisplay = keyDisplay.slice(3);
        } else if (keyDisplay.length === 1) {
            keyDisplay = keyDisplay.toUpperCase();
        }

        parts.push(keyDisplay);

        return isMac ? parts.join(' ') : parts.join(' + ');
    }
}

export { ShortcutManager };
